"use client";

/**
 * usePortfolioEquityCurve Hook
 *
 * Fetches the portfolio-performance-over-time equity curve for the
 * authenticated user. Uses session cookie for auth; wallet address is not
 * required. Mirrors usePortfolio.ts's query patterns.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import type { PortfolioEquityCurvePoint } from "@/types/api.types";

// Stale time: 30 seconds — equity curve is monthly-granularity data, no
// need to refetch as aggressively as allocations/portfolio summary.
const STALE_TIME = 30 * 1000;

export interface UsePortfolioEquityCurveReturn {
  /** Equity curve points; empty when there's no data to show */
  equityCurve: PortfolioEquityCurvePoint[];
  /** User's total allocated amount (starting equity) */
  totalEquity: number;
  /** True once there are at least 2 points — enough to render a line */
  hasCurve: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Whether data is being refetched */
  isFetching: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
}

export function usePortfolioEquityCurve(): UsePortfolioEquityCurveReturn {
  const { isAuthenticated, session } = useAuth();
  const userId = session?.user?.id;

  const query = useQuery({
    queryKey: ["portfolio-equity-curve", userId],
    queryFn: async () => {
      // Session cookie handles auth — no wallet address needed
      return api.allocations.getEquityCurve();
    },
    staleTime: STALE_TIME,
    enabled: isAuthenticated,
  });

  const equityCurve = query.data?.equityCurve ?? [];

  return {
    equityCurve,
    totalEquity: query.data?.totalEquity ?? 0,
    hasCurve: equityCurve.length >= 2,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
